import { Combobox } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Fragment, useMemo, useState } from 'react'
import { cn } from '../lib/utils'

/**
 * AccountCombobox — selector de cuenta corriente con filtro al escribir.
 * Trabaja con el id de la cuenta ('' = sin cuenta vinculada).
 *
 *   <AccountCombobox accounts={cuentas} value={accountId} onChange={setAccountId} balanceLabel="debe" />
 */

export type AccountOption = {
  id: string
  displayName: string
  // null = todavía no tiene cta cte (se crea sola al guardar)
  currentBalance: number | null
}

const norm = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

const money = (n: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(n || 0)

export default function AccountCombobox({
  accounts,
  value,
  onChange,
  balanceLabel = 'debe',
  placeholder = 'Sin cuenta vinculada — escribí para buscar',
  className,
  disabled,
}: {
  accounts: AccountOption[]
  value?: string
  onChange: (accountId: string) => void
  balanceLabel?: string
  placeholder?: string
  className?: string
  disabled?: boolean
}) {
  const [query, setQuery] = useState('')

  const selected = accounts.find((a) => a.id === value) || null

  const filtered = useMemo(() => {
    const q = norm(query.trim())
    if (!q) return accounts
    return accounts.filter((a) => norm(a.displayName).includes(q))
  }, [accounts, query])

  return (
    <Combobox
      value={selected}
      onChange={(a: AccountOption | null) => { onChange(a?.id || ''); setQuery('') }}
      disabled={disabled}
      nullable
    >
      <div className="relative">
        <div className="relative">
          <Combobox.Input
            className={cn(
              'w-full h-9 px-3 pr-8 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed',
              className
            )}
            placeholder={placeholder}
            displayValue={(a: AccountOption | null) => a?.displayName || ''}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Combobox.Button className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <ChevronUpDownIcon className="h-4 w-4" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute z-50 mt-1 max-h-56 w-full min-w-[230px] overflow-auto rounded-lg shadow-lg border bg-card">
          <Combobox.Option value={null} as={Fragment}>
            {({ active }) => (
              <li className={cn('relative cursor-pointer select-none px-3 py-1.5 text-sm text-muted-foreground', active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50')}>
                Sin cuenta vinculada
              </li>
            )}
          </Combobox.Option>
          {filtered.length === 0 && (
            <li className="px-3 py-1.5 text-sm text-muted-foreground select-none">Sin resultados</li>
          )}
          {filtered.map((a) => (
            <Combobox.Option key={a.id} value={a} as={Fragment}>
              {({ active, selected: isSel }) => (
                <li className={cn('relative cursor-pointer select-none px-3 py-1.5 text-sm flex items-center justify-between gap-2', active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50')}>
                  <span className={cn('truncate', isSel ? 'font-semibold' : 'font-normal')}>
                    {a.displayName}
                    <span className="text-muted-foreground font-normal">
                      {a.currentBalance === null ? ' — sin cta cte' : ` — ${balanceLabel} ${money(a.currentBalance)}`}
                    </span>
                  </span>
                  {isSel && <CheckIcon className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />}
                </li>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </div>
    </Combobox>
  )
}
